/* /leave — KPIs, requests (approve/reject), team calendar, balances, policies. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Employee, Leave as LeaveT } from "../../data/types";
import { useAllEmployees, useClock, useDecideLeave, useLeaveBalance, useLeavePolicies, useLeaves, useSettings } from "../../data/hooks";
import { Alert, Bar, Button, Card, Chip, DualDate, EmptyState, ErrorBox, Grid, Kpi, Kv, Loaded, Money, PageHeader, Person, SearchBox, Seg, Skeleton, Tabs, errMsg, useToast } from "../../ui";
import { useI18n } from "../../i18n";
import { csvOf, downloadText, periodLabel } from "../../lib/format";
import { leaveLiability } from "../../domain/payroll";
import { monthEnd, weekday } from "../../domain/dates";
import { LeaveRequestModal } from "./components/LeaveRequestModal";
import { WEEKDAYS } from "./components/labels";
import "./time.css";
import { activeCode, curL } from "../../lib/country";

type Tab = "requests" | "calendar" | "balances" | "policies";
type Filter = "all" | "Pending" | "Approved" | "Rejected";
const PAGE = 15;

export const leaveKpis = (leaves: LeaveT[], emps: Employee[], today: string) => ({
  onLeaveToday: leaves.filter((l) => l.status === "Approved" && l.from <= today && l.to >= today).length,
  pending: leaves.filter((l) => l.status === "Pending").length,
  plannedThisMonth: leaves.filter((l) => l.from.slice(0, 7) === today.slice(0, 7)).length,
  liability: leaveLiability(emps.filter((e) => e.status !== "Terminated"), today, activeCode()),
});

function LeaveTable({ list, emps, canDecide }: { list: LeaveT[]; emps: Map<number, Employee>; canDecide: boolean }) {
  const { L, t } = useI18n();
  const toast = useToast();
  const decide = useDecideLeave();
  const [busy, setBusy] = useState<string>("");
  const go = (l: LeaveT, approve: boolean) => {
    setBusy(l.id + ":" + approve);
    decide.mutate({ id: l.id, approve }, {
      onSuccess: () => toast(approve ? L("Leave approved — attendance and payroll updated", "تم اعتماد الإجازة — تم تحديث الحضور والرواتب") : L("Leave rejected", "تم رفض الإجازة")),
      onError: (e) => toast(errMsg(e), "bad"),
      onSettled: () => setBusy(""),
    });
  };
  if (!list.length) return <EmptyState title={L("No leave requests", "لا توجد طلبات إجازة")} desc={L("New requests will appear here.", "ستظهر الطلبات الجديدة هنا.")} />;
  return (
    <div className="tw"><table className="tbl cards" data-testid="leave-table">
      <thead><tr><th>{t("Employee")}</th><th>{t("Type")}</th><th className="hide-md">{t("From")}</th><th className="hide-md">{t("To")}</th><th className="end">{t("Days")}</th><th>{t("Status")}</th>{canDecide && <th className="end">{t("Actions")}</th>}</tr></thead>
      <tbody>{list.map((l) => {
        const e = emps.get(l.employeeId);
        return (
          <tr key={l.id} data-testid={"leave-" + l.id}>
            <td className="k1">{e ? <Link to={"/employees/" + e.id} style={{ color: "inherit" }}><Person e={e} meta={l.note || undefined} /></Link> : "#" + l.employeeId}</td>
            <td className="k3" data-l={t("Type")}>{t(l.type)}{l.attachmentKey && <span className="xs muted"> · {L("attachment", "مرفق")}</span>}</td>
            <td className="hide-md x" data-l={t("From")}><DualDate date={l.from} /></td>
            <td className="hide-md x" data-l={t("To")}><DualDate date={l.to} /></td>
            <td className="end x" data-l={t("Days")}><span className="num">{l.days}</span></td>
            <td className="k2"><Chip status={l.status} testId={"leave-status-" + l.id} /></td>
            {canDecide && <td className="end k3" data-l="">{l.status === "Pending" && (
              <div className="row nw" style={{ justifyContent: "flex-end" }}>
                <Button variant="ghost sm" onClick={() => go(l, false)} loading={busy === l.id + ":false"} data-testid={"btn-reject-" + l.id}>{t("Reject")}</Button>
                <Button variant="sm" onClick={() => go(l, true)} loading={busy === l.id + ":true"} data-testid={"btn-approve-" + l.id}>{t("Approve")}</Button>
              </div>
            )}</td>}
          </tr>
        );
      })}</tbody>
    </table></div>
  );
}

function TeamCalendar({ leaves, emps, today }: { leaves: LeaveT[]; emps: Map<number, Employee>; today: string }) {
  const i = useI18n();
  const { L, t } = i;
  const [period, setPeriod] = useState(today.slice(0, 7));
  const first = period + "-01", last = monthEnd(first);
  const n = Number(last.slice(8, 10));
  const days = Array.from({ length: n }, (_, k) => period + "-" + String(k + 1).padStart(2, "0"));
  const people = leaves.filter((l) => l.status !== "Rejected" && l.to >= first && l.from <= last);
  const cls = (type: string) => (type === "Annual" ? "" : type === "Sick" ? "sick" : type === "Maternity" ? "mat" : "oth");
  const we = (d: string) => { const w = weekday(d); return w === 5 || w === 6; };
  const shift = (k: number) => { const [y, m] = period.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + k, 1)); setPeriod(d.toISOString().slice(0, 7)); };
  return (
    <div className="card-b" data-testid="team-calendar">
      <div className="row between mb">
        <b>{periodLabel(period, i.lang)}</b>
        <div className="row" style={{ gap: 6 }}>
          <Button variant="ghost sm" icon="chevron-left" onClick={() => shift(-1)} aria-label={L("Previous month", "الشهر السابق")} data-testid="cal-prev" />
          <Button variant="ghost sm" icon="chevron-right" onClick={() => shift(1)} aria-label={L("Next month", "الشهر التالي")} data-testid="cal-next" />
        </div>
      </div>
      {people.length === 0 ? <EmptyState title={L("Nobody is on leave this month", "لا أحد في إجازة هذا الشهر")} /> : (
        <div className="calw"><table className="cal">
          <thead><tr><th />{days.map((d) => <th key={d} className={we(d) ? "we" : ""}>{Number(d.slice(8))}</th>)}</tr></thead>
          <tbody>{people.map((l) => {
            const e = emps.get(l.employeeId);
            return (
              <tr key={l.id} data-testid={"cal-row-" + l.id}>
                <td className="nm">{e ? <Person e={e} meta={t(l.type)} /> : "#" + l.employeeId}</td>
                {days.map((d) => {
                  const on = d >= l.from && d <= l.to;
                  return <td key={d} className={(we(d) ? "we " : "") + (on ? "lv " + cls(l.type) + (l.status === "Pending" ? " pend" : "") : "")} title={on && e ? `${i.name(e)} · ${t(l.type)} · ${t(l.status)}` : undefined} />;
                })}
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
      <div className="legend mt">
        <span><i style={{ background: "var(--primary)" }} />{t("Annual")}</span><span><i style={{ background: "var(--danger)" }} />{t("Sick")}</span>
        <span><i style={{ background: "var(--accent)" }} />{t("Maternity")}</span><span><i style={{ background: "var(--chart-3)" }} />{t("Other")}</span>
        <span><i style={{ background: "var(--warning)" }} />{t("Pending")}</span><span><i style={{ background: "var(--sunken)", border: "1px solid var(--border)" }} />{t("Weekend")}</span>
      </div>
    </div>
  );
}

function BalanceRow({ e }: { e: Employee }) {
  const { L, t } = useI18n();
  const b = useLeaveBalance(e.id);
  return (
    <tr data-testid={"bal-" + e.id}>
      <td className="k1"><Link to={"/employees/" + e.id} style={{ color: "inherit" }}><Person e={e} /></Link></td>
      <td className="end x" data-l={L("Entitlement", "المستحق")}><span className="num">{b.data?.entitlement ?? "…"}</span></td>
      <td className="end x" data-l={L("Used", "المستخدم")}><span className="num">{b.data?.used ?? "…"}</span></td>
      <td className="end k2"><b className="num" data-testid={"bal-rem-" + e.id}>{b.data?.remaining ?? "…"}</b> <span className="xs muted">{t("days")}</span></td>
      <td className="hide-md" style={{ minWidth: 120 }}>{b.data && <Bar value={b.data.used} max={b.data.entitlement} />}</td>
    </tr>
  );
}

function Balances({ emps }: { emps: Employee[] }) {
  const { L, t } = useI18n();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const list = useMemo(() => { const s = q.trim().toLowerCase(); return emps.filter((e) => !s || e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s)); }, [emps, q]);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const cur = Math.min(page, pages);
  return (
    <div data-testid="balances">
      <div className="toolbar"><SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder={t("Search") + "…"} testId="bal-search" /></div>
      {list.length === 0 ? <EmptyState title={L("No employees match", "لا يوجد موظفون مطابقون")} /> : (
        <>
          <div className="tw"><table className="tbl cards">
            <thead><tr><th>{t("Employee")}</th><th className="end">{L("Entitlement", "المستحق")}</th><th className="end">{L("Used", "المستخدم")}</th><th className="end">{L("Remaining", "المتبقي")}</th><th className="hide-md" /></tr></thead>
            <tbody>{list.slice((cur - 1) * PAGE, cur * PAGE).map((e) => <BalanceRow key={e.id} e={e} />)}</tbody>
          </table></div>
          <div className="card-b row between">
            <span className="xs muted">{L(`Annual leave · ${list.length} employees`, `الإجازة السنوية · ${list.length} موظف`)}</span>
            {pages > 1 && <div className="row" style={{ gap: 6 }}>
              <Button variant="sm" disabled={cur <= 1} onClick={() => setPage(cur - 1)} data-testid="bal-prev">{L("Previous", "السابق")}</Button>
              <span className="xs num">{cur} / {pages}</span>
              <Button variant="sm" disabled={cur >= pages} onClick={() => setPage(cur + 1)} data-testid="bal-next">{L("Next", "التالي")}</Button>
            </div>}
          </div>
        </>
      )}
    </div>
  );
}

function Policies() {
  const { L, t } = useI18n();
  const pol = useLeavePolicies();
  const holidays: [string, string, string, number][] = [["Founding Day", "يوم التأسيس", "2027-02-22", 1], ["Eid al-Fitr", "عيد الفطر", "2027-03-09", 4], ["Eid al-Adha", "عيد الأضحى", "2027-05-16", 4], ["Saudi National Day", "اليوم الوطني", "2027-09-23", 1]];
  return (
    <div data-testid="policies">
      <div className="card-b">
        <Grid cols="2">
          <Card title={L("Public holidays (private sector)", "العطل الرسمية (القطاع الخاص)")} sub={L("If a holiday falls on a weekend, a compensating day is given", "إذا صادفت العطلة يوم راحة يُعوَّض بيوم")}>
            {holidays.map((h) => (
              <div className="list-row" key={h[0]}><span className="grow">{L(h[0], h[1])}<div className="xs muted">{L("Eid dates follow the moon sighting", "مواعيد الأعياد حسب رؤية الهلال")}</div></span><DualDate date={h[2]} /><span className="chip">{h[3]} {t("days")}</span></div>
            ))}
          </Card>
          <Card title={L("Balance rules", "قواعد الأرصدة")}>
            <Kv items={[
              [L("Carry-over", "الترحيل"), L("Up to 90 days with employer consent", "حتى 90 يومًا بموافقة صاحب العمل")],
              [L("Encashment", "صرف البدل"), L("Only for unused leave at the end of service", "عند انتهاء الخدمة فقط")],
              [L("Sick pay tiers", "شرائح المرضية"), L("Applied automatically in payroll", "تُطبق تلقائيًا في الرواتب")],
              [L("Ramadan hours", "ساعات رمضان"), L("6 a day / 36 a week, automatic", "6 يوميًا / 36 أسبوعيًا تلقائيًا")],
            ]} />
          </Card>
        </Grid>
      </div>
      <Loaded q={pol}>{(list) => (
        <div className="tw"><table className="tbl cards" data-testid="policies-table">
          <thead><tr><th>{t("Type")}</th><th>{L("Entitlement", "الاستحقاق")}</th><th className="hide-md">{L("Reference", "المرجع")}</th></tr></thead>
          <tbody>{list.map((p) => <tr key={p.type}><td className="k1"><b>{t(p.type)}</b></td><td className="k3" data-l="">{L(p.en, p.ar)}</td><td className="hide-md muted">{L("Labor Law ", "نظام العمل ")}{p.ref}</td></tr>)}</tbody>
        </table></div>
      )}</Loaded>
      <div className="card-b"><Alert tone="info" tint title={L("Ramadan hours", "ساعات رمضان")} desc={L("Muslim employees work at most 6 hours a day, 36 a week, during Ramadan. Attendance rules switch automatically.", "يعمل الموظفون المسلمون 6 ساعات يوميًا كحد أقصى (36 أسبوعيًا) في رمضان، وتتغير قواعد الحضور تلقائيًا.")} /></div>
    </div>
  );
}

export default function Leave() {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const { today } = useClock();
  const leaves = useLeaves();
  const emps = useAllEmployees();
  const pols = useLeavePolicies();
  const settings = useSettings();
  const [tab, setTab] = useState<Tab>("requests");
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState<null | "new" | "ai">(null);
  const byId = useMemo(() => new Map((emps.data ?? []).map((e) => [e.id, e])), [emps.data]);

  const weekend = settings.data?.attendance.weekendDays ?? [];
  const weekendText = weekend.map((d) => L(WEEKDAYS[d][0], WEEKDAYS[d][1])).join(L(" & ", " و"));
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (leaves.data ?? []).filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!s) return true;
      const e = byId.get(l.employeeId);
      return l.type.toLowerCase().includes(s) || (!!e && (e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s)));
    });
  }, [leaves.data, filter, q, byId]);

  const report = () => {
    const rows = (leaves.data ?? []).map((l) => { const e = byId.get(l.employeeId); return [l.id, e?.code ?? "", e?.nameEn ?? "", e?.nameAr ?? "", l.type, l.from, l.to, l.days, l.status, l.note]; });
    downloadText(`leave-report-${today}.csv`, csvOf([["ID", "Code", "Employee", "Employee (AR)", "Type", "From", "To", "Days", "Status", "Note"], ...rows]));
    toast(L("Leave report exported", "تم تصدير تقرير الإجازات"));
  };

  if (leaves.error) return <ErrorBox error={leaves.error} onRetry={() => leaves.refetch()} />;
  if (emps.error) return <ErrorBox error={emps.error} onRetry={() => emps.refetch()} />;
  if (!leaves.data || !emps.data) return <Skeleton />;
  const k = leaveKpis(leaves.data, emps.data, today);
  const tabs: [Tab, React.ReactNode, (number | null)?][] = [["requests", t("Requests"), k.pending || null], ["calendar", t("Team calendar")], ["balances", t("Balances")], ["policies", t("Policies")]];

  return (
    <div data-testid="leave-page">
      <PageHeader title={t("Leave")}
        sub={L(`Saudi Labor Law defaults · weekend: ${weekendText || "—"}`, `وفق نظام العمل السعودي · العطلة: ${weekendText || "—"}`)}
        actions={<>
          <Button icon="download" onClick={report} data-testid="btn-leave-report">{L("Leave report", "تقرير الإجازات")}</Button>
          <Button className="aibtn" icon="sparkle" onClick={() => setModal("ai")} data-testid="btn-leave-ai">{L("From a message", "من رسالة")}</Button>
          <Button variant="pri" icon="plus" onClick={() => setModal("new")} data-testid="btn-new-leave">{t("New request")}</Button>
        </>} />
      <div className="g g-4 mb" data-testid="leave-kpis">
        <Kpi label={t("On leave today")} value={String(k.onLeaveToday)} icon="plane" testId="kpi-on-leave" />
        <Kpi label={t("Pending")} value={String(k.pending)} icon="inbox" tone="warn" testId="kpi-pending" />
        <Kpi label={L("Planned this month", "مخطط هذا الشهر")} value={String(k.plannedThisMonth)} icon="calendar" tone="info" testId="kpi-planned" />
        <Kpi label={L("Leave liability", "التزام الإجازات")} value={<span data-testid="kpi-liability-v"><Money value={k.liability} dec={0} plain /></span>} unit={curL(L)} icon="wallet" tone="gold" foot={L("Unused annual leave × daily wage", "الإجازات غير المستخدمة × الأجر اليومي")} testId="kpi-liability" />
      </div>
      <section className="card">
        <Tabs<Tab> items={tabs} value={tab} onChange={setTab} label={t("Leave")} />
        {tab === "requests" && (
          <>
            <div className="toolbar">
              <SearchBox value={q} onChange={setQ} placeholder={t("Search") + "…"} testId="leave-search" />
              <Seg<Filter> value={filter} onChange={setFilter} label={t("Status")} items={[["all", t("All")], ["Pending", t("Pending")], ["Approved", t("Approved")], ["Rejected", t("Rejected")]]} />
            </div>
            <LeaveTable list={filtered} emps={byId} canDecide />
          </>
        )}
        {tab === "calendar" && <TeamCalendar leaves={leaves.data} emps={byId} today={today} />}
        {tab === "balances" && <Balances emps={emps.data.filter((e) => e.status !== "Terminated")} />}
        {tab === "policies" && <Policies />}
      </section>
      {modal && (pols.data ? <LeaveRequestModal policies={pols.data} today={today} aiFirst={modal === "ai"} onClose={() => setModal(null)} /> : null)}
    </div>
  );
}
